import React, { useEffect, useState } from "react";
import { db } from "./firebase/firebaseConfig";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  query,
  where,
} from "firebase/firestore";
import { getAuth, createUserWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import Header from "./components/Header";
import { useTranslation } from "react-i18next";
import { useBanner } from "./components/BannerContext";

function AdminDashboard({ user }) {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const { showBanner } = useBanner();
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    phone: "",
    email: "",
    role: "",
    coachId: "",
    therapistId: "",
    club: "",
    password: "",
    allocatedHours: "",
    birthDate: "",
  });
  const { t } = useTranslation();
  const [coaches, setCoaches] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [nutritionists, setNutritionists] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const auth = getAuth();
  const updatePastSessionsWithNewCoach = async (athleteId, newCoachId) => {
    try {
      const sessionsRef = collection(db, "sessions");
      const q = query(sessionsRef, where("athleteId", "==", athleteId));
      const snapshot = await getDocs(q);

      const updatePromises = snapshot.docs.map((docSnap) =>
        updateDoc(docSnap.ref, { coachId: newCoachId })
      );

      await Promise.all(updatePromises);
      console.log(`✅ Updated ${snapshot.size} session(s) with new coach ID.`);
    } catch (error) {
      console.error("❌ Error updating sessions:", error.message);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      const snapshot = await getDocs(collection(db, "users"));
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setUsers(data);
      setCoaches(data.filter((user) => user.role === "coach"));
      setTherapists(data.filter((user) => user.role === "therapist"));
      setNutritionists(data.filter((user) => user.role === "nutritionist"));
    };
    fetchUsers();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const newUser = {
        ...formData,
        allocatedHours: formData.role === "athlete" ? parseFloat(formData.allocatedHours || "0") : 0,
        usedHours: 0,
      };

      if (newUser.role !== "athlete") {
        delete newUser.coachId;
        delete newUser.therapistId;
      } else if (!newUser.therapistId) {
        alert("Athletes must be assigned a therapist.");
        return;
      }

      // Check for duplicate email in Firestore list
      const emailExists = users.some(
        (user) =>
          user.email.toLowerCase() === newUser.email.toLowerCase() &&
          user.id !== newUser.id
      );
      if (emailExists) {
        alert("❌ A user with this email already exists.");
        return;
      }

      if (isEditing) {
        const userRef = doc(db, "users", newUser.id);
        await updateDoc(userRef, newUser);

        // 🔁 Check if coach changed for an athlete and update past sessions
        const originalUser = users.find((u) => u.id === newUser.id);
        if (
          newUser.role === "athlete" &&
          newUser.coachId &&
          newUser.coachId !== originalUser.coachId
        ) {
          await updatePastSessionsWithNewCoach(newUser.id, newUser.coachId);
        }

        // 🔐 Update Firebase Auth if email or password changed
        const authUpdates = {};
        if (newUser.email !== originalUser.email) authUpdates.email = newUser.email;
        if (newUser.password && newUser.password.length >= 6) authUpdates.password = newUser.password;

        if (Object.keys(authUpdates).length > 0) {
          try {
            const response = await fetch("https://point.atharadvisory.com/api/update-user", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer supersecretathar",
              },
              body: JSON.stringify({
                uid: newUser.id,
                ...authUpdates,
              }),
            });

            const result = await response.json();
            if (!response.ok) throw new Error(result.error || "Unknown error");

            alert("✅ Firebase Auth user updated successfully.");
          } catch (error) {
            alert("❌ Error updating Firebase Auth user: " + error.message);
          }
        }

        alert("✅ User updated successfully!");
      } else {
        try {
          const response = await fetch("https://point.atharadvisory.com/api/create-user", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": "Bearer supersecretathar", // Must match your VPS .env
            },
            body: JSON.stringify(formData),
          });

          const result = await response.json();
          if (!response.ok) throw new Error(result.error || "Unknown error");
          await sendPasswordResetEmail(auth, formData.email);

          showBanner("✅ User created and password setup email sent.", "success");
        } catch (error) {
          alert("❌ Error creating user: " + error.message);
        }
      }

      setFormData({ id: "", name: "", phone: "", email: "", role: "", coachId: "", therapistId: "", club: "", password: "", birthDate: "", allocatedHours: "" });
      setShowForm(false);
      setIsEditing(false);

      const snapshot = await getDocs(collection(db, "users"));
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setUsers(data);
      setCoaches(data.filter((user) => user.role === "coach"));
      setTherapists(data.filter((user) => user.role === "therapist"));
    } catch (error) {
      alert("❌ Error saving user: " + error.message);
    }
  };

  const handleEdit = (user) => {
    setFormData({ ...user });
    setShowForm(true);
    setIsEditing(true);
  };

  const handleDelete = async (userId) => {
    const confirmed = window.confirm("Are you sure you want to delete this user?");
    if (!confirmed) return;

    try {
      const response = await fetch("https://point.atharadvisory.com/api/delete-user", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer supersecretathar",
        },
        body: JSON.stringify({ uid: userId }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Unknown error");

      setUsers((prev) => prev.filter((user) => user.id !== userId));
      showBanner("🗑️ User deleted from Firebase Auth and Firestore.", "success");
    } catch (error) {
      alert("❌ Error deleting user: " + error.message);
    }
  };

  return (
    <>
      <Header user={user} />
      <div className="min-h-screen bg-white px-4 py-8">
        <div className="max-w-5xl mx-auto bg-[#f1efe7] rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">👤 Admin Dashboard</h2>

          <button
            onClick={() => {
              setShowForm(!showForm);
              setFormData({ id: "", name: "", phone: "", email: "", role: "", coachId: "", therapistId: "", club: "", password: "", });
              setIsEditing(false);
            }}
            className="mb-6 bg-[#b22222] text-white px-4 py-2 rounded-md hover:bg-red-800 transition"
          >
            {showForm ? "Cancel" : "➕ Create New User"}
          </button>

          {showForm && (
            <form onSubmit={handleSubmit} className="space-y-4 mb-8 bg-white p-4 rounded-lg shadow">
              <input
                name="name"
                placeholder="Full Name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
              <input
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
              <input
                name="email"
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
              <input
                name="password"
                type="password"
                placeholder="New Password (leave blank to keep current)"
                value={formData.password || ""}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                <option value="">-- Select Role --</option>
                <option value="athlete">Athlete</option>
                <option value="coach">Coach</option>
                <option value="therapist">Therapist</option>
                <option value="nutritionist">Nutritionist</option>
                <option value="admin">Admin</option>
                <option value="client">Client</option>
              </select>
              {(formData.role === "coach") && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Club</label>
                  <input
                    name="club"
                    type="text"
                    value={formData.club}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="e.g., Al Hilal FC"
                  />
                </div>
              )}

              {formData.role === "athlete" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <select
                    name="coachId"
                    value={formData.coachId}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  >
                    <option value="">-- Optionally Assign Coach --</option>
                    {coaches.map((coach) => (
                      <option key={coach.id} value={coach.id}>
                        {coach.name || coach.email}
                      </option>
                    ))}
                  </select>

                  <select
                    name="therapistId"
                    value={formData.therapistId}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    required
                  >
                    <option value="">-- Assign Therapist (Required) --</option>
                    {therapists.map((therapist) => (
                      <option key={therapist.id} value={therapist.id}>
                        {therapist.name || therapist.email}
                      </option>
                    ))}
                  </select>

                  <select
                    name="assignedNutritionist"
                    value={formData.assignedNutritionist || ""}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  >
                    <option value="">-- Optionally Assign Nutritionist --</option>
                    {nutritionists.map((nutri) => (
                      <option key={nutri.id} value={nutri.id}>
                        {nutri.name || nutri.email}
                      </option>
                    ))}
                  </select>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Club</label>
                    <input
                      name="club"
                      type="text"
                      value={formData.club}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="e.g., Al Hilal FC"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Allocated Hours</label>
                    <input
                      name="allocatedHours"
                      type="number"
                      step="0.25"
                      min="0"
                      value={formData.allocatedHours}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="e.g., 10"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Birth Date</label>
                    <input
                      name="birthDate"
                      type="date"
                      value={formData.birthDate}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="YYYY-MM-DD"
                    />
                  </div>

                </div>
              )}

              <button
                type="submit"
                className="bg-[#b22222] text-white px-4 py-2 rounded-md hover:bg-red-800 transition"
              >
                {isEditing ? "💾 Update User" : "✅ Save User"}
              </button>
            </form>
          )}

          <h3 className="text-lg font-semibold text-gray-800 mb-2">Existing Users</h3>
          <ul className="divide-y divide-gray-200">
            {users.map((user) => (
              <li key={user.id} className="py-2 flex justify-between items-center">
                <div>
                  <div>
                    <strong>{user.name || "Unnamed User"}</strong> — {user.email} ({user.role})
                    {user.phone && <> — 📞 {user.phone}</>}
                  </div>

                  {user.role === "athlete" && (
                    <div className="mt-2 text-sm text-gray-700">
                      ⏱ Allocated Hours: {user.allocatedHours || 0}

                      <form
                        onSubmit={async (e) => {
                          e.preventDefault();
                          const amountToAdd = parseFloat(e.target.elements[`hours-${user.id}`].value || "0");
                          if (isNaN(amountToAdd) || amountToAdd <= 0) {
                            alert("Please enter a valid number of hours to add.");
                            return;
                          }

                          try {
                            const newTotal = (user.allocatedHours || 0) + amountToAdd;
                            await updateDoc(doc(db, "users", user.id), {
                              allocatedHours: newTotal,
                            });

                            alert(`✅ Added ${amountToAdd} hour(s) for ${user.name}.`);

                            // Refresh user list
                            const snapshot = await getDocs(collection(db, "users"));
                            const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
                            setUsers(data);
                          } catch (error) {
                            alert("❌ Failed to update hours: " + error.message);
                          }
                        }}
                        className="flex items-center gap-2 mt-1"
                      >
                        <input
                          type="number"
                          step="0.25"
                          min="0"
                          name={`hours-${user.id}`}
                          placeholder="Add hours"
                          className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                        <button
                          type="submit"
                          className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          ➕ Add
                        </button>
                      </form>
                    </div>
                  )}
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={() => handleEdit(user)}
                    className="text-sm text-[#b22222] hover:underline"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => handleDelete(user.id)}
                    className="text-sm text-red-600 hover:underline"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}

export default AdminDashboard;
